import React from 'react';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import HelpOutline from '@mui/icons-material/HelpOutline';
import IconButton from '@mui/material/IconButton';
import { useShoppingCart } from '../../hooks';
import useShoppingCartSummary from '../../hooks/useShoppingCartSummary';
import useIsMobile from '../../hooks/useIsMobile';
import RenderSummaryItems from './RenderSummaryItems';
import SummaryInfoDrawer from './SummaryInfoDrawer';
import { DEFAULT_ICON_CLASS_NAME } from '../../theme/constants/icon';
import { SHOPPING_CART_SUMMARY_TITLE_CLASS_NAME } from '../../theme/constants/typography';

const SummaryData = ({
  cartType,
  title,
  disclaimer,
  infoTitle,
  info,
  WarningAlert,
  getWarningParams,
}) => {
  const isMobile = useIsMobile();
  const { shoppingCartData, loading } = useShoppingCart({ cartType });
  const summary = useShoppingCartSummary(shoppingCartData);
  const warningParams = getWarningParams(shoppingCartData);

  const [drawerOpen, setDrawerOpen] = React.useState(false);

  return (
    <Paper sx={{ backgroundColor: 'gray.A100', borderRadius: 2, p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'gray.A200' }} elevation={0}>
      <Grid container rowSpacing={1}>
        <Grid item xs={12} textAlign="center" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Grid item xs={info ? 11 : 12}>
            <Typography
              variant={isMobile ? 'medium_2' : 'medium_3'}
              className={SHOPPING_CART_SUMMARY_TITLE_CLASS_NAME}
            >
              {title}
            </Typography>
          </Grid>
          {info && (
            <Grid item xs={1} textAlign="right">
              <IconButton onClick={() => setDrawerOpen(true)} size="small" id={`${cartType}-summary-info-button`}>
                <HelpOutline className={DEFAULT_ICON_CLASS_NAME} sx={{ fontSize: 20, cursor: 'pointer' }} />
              </IconButton>
              <SummaryInfoDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                infoTitle={infoTitle}
                info={info}
              />
            </Grid>
          )}
        </Grid>
        {WarningAlert && (
          <Grid item xs={12}>
            <WarningAlert {...warningParams} />
          </Grid>
        )}
        {RenderSummaryItems(summary, loading)}
        <Grid item xs={12}>
          <Typography variant={isMobile ? 'small_1' : 'small_2'} fontWeight={500} color="text.secondary">{disclaimer}</Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

SummaryData.propTypes = {
  cartType: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  disclaimer: PropTypes.string,
  infoTitle: PropTypes.string,
  info: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })),
  WarningAlert: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  getWarningParams: PropTypes.func,
};

SummaryData.defaultProps = {
  disclaimer: null,
  infoTitle: null,
  info: null,
  WarningAlert: null,
  getWarningParams: () => null,
};

export default SummaryData;
